import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import posthog from '@/lib/posthog';
import { calcLoanDurationDays } from '@/lib/loanDuration';
import {
  downloadFile,
  exportItemsAsCSV,
  exportItemsAsJSON,
  exportLoanHistoryAsCSV,
  exportLoanHistoryAsJSON,
  type ExportableItem,
  type ExportableLoan,
} from '@/lib/exportData';
import { toast } from 'sonner';

type ExportFormat = 'json' | 'csv';

/** Safety cap to prevent the browser from OOMing on huge exports. */
const EXPORT_RECORD_CAP = 5000;

/**
 * Provides `exportItems` and `exportLoanHistory` functions for downloading
 * the current user's data as CSV or JSON.
 *
 * Both functions fetch all records in a single request (no pagination) and
 * trigger a browser file download via a temporary `<a>` element. A safety
 * cap of {@link EXPORT_RECORD_CAP} prevents the browser from running out of
 * memory on very large accounts.
 *
 * `exportLoanHistory` requires three separate Supabase queries because:
 *   1. RLS on `loans` only returns rows where the caller is the borrower, so
 *      we first fetch owned item IDs, then query loans for those items.
 *   2. Item names for borrowed items are resolved in a follow-up query since
 *      they weren't included in the initial items fetch.
 *   3. Profile display names are fetched last, once all unique user IDs are known.
 *
 * `duration_days` and `overdue_days` are derived fields calculated client-side
 * from loan timestamps — they are not stored in the database.
 *
 * @returns An object containing:
 *   - `exportItems` — downloads the user's items as CSV or JSON.
 *   - `exportLoanHistory` — downloads the user's loan history as CSV or JSON.
 *   - `isExportingItems` — loading flag for the items export.
 *   - `isExportingLoans` — loading flag for the loan history export.
 */
export function useExportData() {
  const { user } = useAuth();
  const [isExportingItems, setIsExportingItems] = useState(false);
  const [isExportingLoans, setIsExportingLoans] = useState(false);

  const exportItems = async (format: ExportFormat) => {
    if (!user) return;
    setIsExportingItems(true);
    try {
      const { data, error } = await supabase
        .from('items')
        .select(
          'name, category, condition, status, brand, location_label, circulation_mode, tags, description',
        )
        .eq('owner_id', user.id)
        .limit(EXPORT_RECORD_CAP);

      if (error) throw error;

      if ((data?.length ?? 0) >= EXPORT_RECORD_CAP) {
        toast.warning(`Export limited to ${EXPORT_RECORD_CAP} records.`);
      }

      const items: ExportableItem[] = (data ?? []).map((i) => ({
        name: i.name,
        category: i.category,
        condition: i.condition,
        status: i.status,
        brand: i.brand,
        location: i.location_label, // DB column is `location_label`; exported as `location` for readability
        circulation_mode: i.circulation_mode,
        tags: i.tags ?? [],
        description: i.description,
      }));

      if (items.length === 0) {
        toast.info('No items to export.');
        return;
      }

      const timestamp = new Date().toISOString().slice(0, 10);
      if (format === 'csv') {
        downloadFile(
          exportItemsAsCSV(items),
          `shareshack-items-${timestamp}.csv`,
          'text/csv;charset=utf-8',
        );
      } else {
        downloadFile(
          exportItemsAsJSON(items),
          `shareshack-items-${timestamp}.json`,
          'application/json',
        );
      }
      posthog.capture('data_exported', { dataset: 'items', format });
      toast.success(`Items exported as ${format.toUpperCase()}.`);
    } catch (err) {
      toast.error('Failed to export items', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsExportingItems(false);
    }
  };

  const exportLoanHistory = async (format: ExportFormat) => {
    if (!user) return;
    setIsExportingLoans(true);
    try {
      // Fetch loans where user is owner of item OR borrower
      const { data: ownedItems } = await supabase
        .from('items')
        .select('id, name')
        .eq('owner_id', user.id);

      const ownedItemIds = (ownedItems ?? []).map((i) => i.id);
      const itemNameMap = new Map((ownedItems ?? []).map((i) => [i.id, i.name]));

      // Loans as owner
      let ownerLoans: any[] = [];
      if (ownedItemIds.length > 0) {
        const { data } = await supabase
          .from('loans')
          .select(
            'id, item_id, borrower_id, lender_user_id, borrowed_at, returned_at, due_at, status, notes',
          )
          .in('item_id', ownedItemIds)
          .limit(EXPORT_RECORD_CAP);
        ownerLoans = data ?? [];
      }

      // Loans as borrower
      const { data: borrowerLoans } = await supabase
        .from('loans')
        .select(
          'id, item_id, borrower_id, lender_user_id, borrowed_at, returned_at, due_at, status, notes',
        )
        .eq('borrower_id', user.id)
        .limit(EXPORT_RECORD_CAP);

      // [LEARN] Three queries are needed because Supabase RLS only returns loans where you're the
      // borrower; owner loans require joining through the items table. We then deduplicate via Map.
      const allLoansMap = new Map<string, any>();
      for (const l of [...ownerLoans, ...(borrowerLoans ?? [])]) {
        allLoansMap.set(l.id, l);
      }
      const allLoans = Array.from(allLoansMap.values());

      if (allLoans.length >= EXPORT_RECORD_CAP) {
        toast.warning(`Export limited to ${EXPORT_RECORD_CAP} records.`);
      }

      if (allLoans.length === 0) {
        toast.info('No loan history to export.');
        return;
      }

      // Fetch missing item names
      const missingItemIds = allLoans
        .filter((l) => !itemNameMap.has(l.item_id))
        .map((l) => l.item_id);
      if (missingItemIds.length > 0) {
        const { data: extraItems } = await supabase
          .from('items')
          .select('id, name')
          .in('id', [...new Set(missingItemIds)]);
        for (const i of extraItems ?? []) {
          itemNameMap.set(i.id, i.name);
        }
      }

      // Fetch profile names
      const userIds = new Set<string>();
      for (const l of allLoans) {
        if (l.borrower_id) userIds.add(l.borrower_id);
        if (l.lender_user_id) userIds.add(l.lender_user_id);
      }
      const profileMap = new Map<string, string>();
      if (userIds.size > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', [...userIds]);
        for (const p of profiles ?? []) {
          if (p.display_name) profileMap.set(p.user_id, p.display_name);
        }
      }

      const exportLoans: ExportableLoan[] = allLoans.map((l) => {
        // Cancelled loans have no meaningful duration — the item was never
        // (or should not still be) out on loan.
        const isCancelled = l.status === 'CANCELLED';
        const endDate = l.returned_at ? new Date(l.returned_at) : new Date();
        const startDate = new Date(l.borrowed_at);
        const durationDays = isCancelled ? 0 : calcLoanDurationDays(startDate, endDate);

        // Overdue days: null when no due date was set, or when the loan was
        // returned/completed on time.  For active overdue loans, `compareDate`
        // is now; for returned loans it is the actual return date so we capture
        // the peak overdue count at the time the item came back.
        let overdueDays: number | null = null;
        if (l.due_at && !isCancelled) {
          const compareDate = l.returned_at ? new Date(l.returned_at) : new Date();
          const deadline = new Date(l.due_at);
          if (compareDate > deadline) {
            overdueDays = Math.ceil((compareDate.getTime() - deadline.getTime()) / 86400000);
          }
        }

        return {
          item_name: itemNameMap.get(l.item_id) ?? 'Unknown',
          borrower: profileMap.get(l.borrower_id) ?? l.borrower_id,
          lender: l.lender_user_id ? (profileMap.get(l.lender_user_id) ?? l.lender_user_id) : null,
          borrowed_at: l.borrowed_at,
          returned_at: l.returned_at,
          due_at: l.due_at,
          duration_days: durationDays,
          overdue_days: overdueDays,
          status: l.status,
          note: l.notes ?? null,
        };
      });

      const timestamp = new Date().toISOString().slice(0, 10);
      if (format === 'csv') {
        downloadFile(
          exportLoanHistoryAsCSV(exportLoans),
          `shareshack-loan-history-${timestamp}.csv`,
          'text/csv;charset=utf-8',
        );
      } else {
        downloadFile(
          exportLoanHistoryAsJSON(exportLoans),
          `shareshack-loan-history-${timestamp}.json`,
          'application/json',
        );
      }
      posthog.capture('data_exported', { dataset: 'loan_history', format });
      toast.success(`Loan history exported as ${format.toUpperCase()}.`);
    } catch (err) {
      toast.error('Failed to export loan history', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsExportingLoans(false);
    }
  };

  return { exportItems, exportLoanHistory, isExportingItems, isExportingLoans };
}
